import { Box, Center, Flex, Grid, Image, Spinner, Text, useDisclosure, useToast } from '@chakra-ui/react'
import { Button as UbiButton, Button, Text as UbiText } from '@ubirider/pick-component-library'
import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { Card } from './Card'
import { DeleteCardModal } from '../../../../modals/DeleteCardModal'
import { RedirectModal } from '../../../../modals/RedirectModal'
import { deleteCard } from '../../../../api/cards'
import { getVivaWalletPaymentInfo } from '../../../../api/payment'
import { useCards, useUser } from '../../../../context/DataProvider'

import EmptyStateIllustration from '../../../../assets/card_empty.svg'
import CheckIcon from '../../../../assets/check_circle_white.svg'
import CloseIcon from '../../../../assets/cross_white_24.svg'
import PlusIcon from '../../../../assets/plus_white.svg'

const MFlex = motion.create(Flex)
const MGrid = motion.create(Grid)
const MText = motion.create(Text)
const MBox = motion.create(Box)
const MUbiText = motion.create(UbiText)

const variants = {
  container: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.05 },
    },
  },
  item: {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 25 } },
  },
}

export const CardsMenu = ({ loading }) => {
  const { t } = useTranslation()
  const toast = useToast()

  const { user } = useUser()
  const { cards, refetch } = useCards()

  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isOpenRedirect, onOpen: onOpenRedirect, onClose: onCloseRedirect } = useDisclosure()

  const [showAllCards, setShowAllCards] = useState(false)
  const [removeLoading, setRemoveLoading] = useState(false)
  const [transactionID, setTransactionID] = useState(null)
  const [orderCode, setOrderCode] = useState(null)
  const [loadingVVW, setLoadingVVW] = useState(false)

  const onConfirm = async () => {
    setRemoveLoading(true)
    try {
      await deleteCard(transactionID)
      await refetch()
      toast({
        position: 'top',
        duration: 5000,
        render: ({ onClose: closeToast }) => (
          <Flex align='center' justify='space-between' bg='#01AE70' p='16px' rounded='4px' color='white' gap='16px'>
            <Image src={CheckIcon} />
            <Text fontSize='14px' fontWeight='600'>
              {t('home.payment_card_deleted')}
            </Text>
            <Image cursor='pointer' onClick={closeToast} src={CloseIcon} />
          </Flex>
        ),
      })
      onClose()
    } catch (error) {
      toast({
        position: 'top',
        duration: 5000,
        render: ({ onClose: closeToast }) => (
          <Flex align='center' justify='space-between' bg='danger.600' p='16px' rounded='4px' color='white' gap='16px'>
            <Image src={CheckIcon} />
            <Text fontSize='14px' fontWeight='600'>
              Erro a eliminar o cartão
            </Text>
            <Image cursor='pointer' onClick={closeToast} src={CloseIcon} />
          </Flex>
        ),
      })
    } finally {
      setRemoveLoading(false)
      setTransactionID(null)
    }
  }

  const handleAddPayment = async () => {
    try {
      setLoadingVVW(true)
      const res = await getVivaWalletPaymentInfo(user, true)
      setOrderCode(res.data.orderCode)
      onOpenRedirect()
    } catch (err) {
      console.error(err)
    } finally {
      setLoadingVVW(false)
    }
  }

  const displayedCards = useMemo(() => {
    if (!cards) return []
    return showAllCards ? cards : cards.slice(0, 2)
  }, [cards, showAllCards])

  return (
    <MFlex
      bg={{ base: 'transparent', md: 'neutral.100' }}
      rounded='8px'
      p={{ base: 0, md: '24px' }}
      direction='column'
      w='100%'
      gap='24px'
      variants={variants.container}
      initial='hidden'
      animate='visible'
    >
      <MText variants={variants.item} fontWeight='700' fontSize='24px'>
        {t('acqua_vias.payment_methods_title')}
      </MText>

      {loading ? (
        <Center minH='240px'>
          <Spinner thickness='6px' color='neutral.500' size='xl' />
        </Center>
      ) : cards?.length > 0 ? (
        <Flex direction='column' gap='16px'>
          <MBox variants={variants.item}>
            <Button
              onClick={handleAddPayment}
              style='ghost'
              type='secondary'
              size='small'
              leftIconProps={{ src: PlusIcon }}
              label={t('home.add_payment_method')}
              loading={loadingVVW}
              css={{ padding: '16px 4px' }}
            />
          </MBox>

          <MGrid
            variants={variants.item}
            templateColumns={{
              base: `repeat(${cards.length}, 280px)`,
              sm: 'repeat(1, 100%)',
              md: 'repeat(2, 1fr)',
              xxl: 'repeat(2, 1fr)',
            }}
            gap='24px'
            overflowX={{ base: 'auto', md: 'visible' }}
            mx={{ base: '-24px', md: 0 }}
            px={{ base: '24px', md: 0 }}
            pb={{ base: '24px', md: 0 }}
            sx={{ '&::-webkit-scrollbar': { display: 'none' } }}
          >
            <AnimatePresence mode='popLayout'>
              {(window.innerWidth < 768 ? cards : displayedCards).map(card => (
                <MBox
                  key={card.cardUniqueReference}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <Card
                    card={card}
                    onRemove={() => {
                      setTransactionID(card.recurringTransactionID)
                      onOpen()
                    }}
                  />
                </MBox>
              ))}
            </AnimatePresence>
          </MGrid>

          {cards.length > 2 && (
            <Flex justify='flex-end' display={{ base: 'none', md: 'flex' }}>
              <Button
                onClick={() => setShowAllCards(!showAllCards)}
                style='ghost'
                type='secondary'
                label={showAllCards ? t('home.see_less_methods') : t('home.see_all_methods')}
              />
            </Flex>
          )}
        </Flex>
      ) : (
        <EmptyState t={t} onAdd={handleAddPayment} loading={loadingVVW} />
      )}

      <DeleteCardModal isOpen={isOpen} onClose={onClose} onConfirm={onConfirm} loading={removeLoading} />
      <RedirectModal isOpen={isOpenRedirect} onClose={onCloseRedirect} orderCode={orderCode} />
    </MFlex>
  )
}

const EmptyState = ({ t, onAdd, loading }) => (
  <MFlex
    key='empty'
    direction='column'
    my={{ sm: '', md: '48px', lg: '48px', xl: '48px' }}
    mx={{ sm: '', md: '96px', lg: '96px', xl: '96px' }}
    justifyContent='center'
    alignItems='center'
    textAlign='center'
    gap='24px'
    variants={variants.card}
  >
    <motion.div variants={variants.header}>
      <Image src={EmptyStateIllustration} height='160px' />
    </motion.div>

    <MUbiText type='textSM' css={{ textAlign: 'center' }} variants={variants.header}>
      {t('acqua_vias.payment_methods_empty_state')}
    </MUbiText>

    <Center>
      <motion.div variants={variants.buttonLate}>
        <Flex display={{ sm: 'none', lg: 'flex' }}>
          <UbiButton
            onClick={onAdd}
            style='outline'
            type='secondary'
            loading={loading}
            leftIconProps={{ src: PlusIcon }}
            label={t('home.add_payment_method')}
          />
        </Flex>
        <Flex display={{ sm: 'flex', lg: 'none' }}>
          <UbiButton
            onClick={onAdd}
            style='outline'
            size='small'
            type='secondary'
            loading={loading}
            leftIconProps={{ src: PlusIcon }}
            label={t('home.add_payment_method')}
          />
        </Flex>
      </motion.div>
    </Center>
  </MFlex>
)
